import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    Box,
    Chip,
    FormControlLabel,
    FormHelperText,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import HowToRegOutlinedIcon from '@material-ui/icons/HowToRegOutlined'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'

import type { WorkbookSectionsState } from '../PipelineFlowDemo/workbookSectionDraft'
import {
    CONDITIONAL_METHOD_META,
    ConditionalApprovalsPolicy,
    ConditionalMethodId,
    methodsForStage,
} from './conditionalApprovals'
import { LeasePacketMember, describeLeasePacketRule } from './signableDocRoles'
import {
    PhaseApplicantBinding,
    PhaseApplicantBindingsMap,
    PhaseApplicantDataBindings,
} from './PhaseApplicantDataBindings'
import { PHASE_PANEL_TONES, PhaseControlPanel } from './PhaseControlPanel'

/**
 * Compose prototype: fixed-phase Leads Pipeline timeline body.
 * Selection chrome / chooser live in LeadsPipelineComposeTab.
 * Flowchart UI remains in `PipelineFlowDemo` (unmounted from this shell).
 */

export type ScreenPlacement = 'before_signing' | 'after_signing'

export type FeeTiming =
    | 'before_application'
    | 'on_submit'
    | 'before_screening'
    | 'after_screening'
    | 'before_lease'
    | 'after_lease'
    | 'never'

type PhaseId =
    | 'leads'
    | 'application'
    | 'screening'
    | 'qualification'
    | 'lease'
    | 'future_resident'

type GateMode = 'auto' | 'pm_review'
type AccountInviteMode = 'auto' | 'manager'
type CheckTiming = 'after_application' | 'before_lease' | 'after_lease'

const FEE_TIMING_LABELS: Record<FeeTiming, string> = {
    before_application: 'Before application starts',
    on_submit: 'On application submit',
    before_screening: 'Before screening',
    after_screening: 'After screening / before lease',
    before_lease: 'Before lease signing',
    after_lease: 'After lease signing',
    never: 'Never / external',
}

function isZeroFeeAmount(amount?: string): boolean {
    const parsed = Number.parseFloat(amount ?? '')
    return Number.isFinite(parsed) && parsed === 0
}

const CHECK_TIMING_LABELS: Record<CheckTiming, string> = {
    after_application: 'After application is complete',
    before_lease: 'Before lease signing',
    after_lease: 'After lease signing',
}

function defaultCheckTiming(placement: ScreenPlacement): CheckTiming {
    return placement === 'before_signing' ? 'after_application' : 'after_lease'
}

const PHASE_COPY: Record<
    PhaseId,
    { label: string; summary: string; alwaysOn?: boolean }
> = {
    leads: {
        label: 'Leads',
        summary:
            'Leads are prospective tenants who have filled out a guest card.',
        alwaysOn: true,
    },
    application: {
        label: 'Application',
        summary: 'Workbook, application docs, and fee timing.',
        alwaysOn: true,
    },
    screening: {
        label: 'Screening',
        summary:
            'Which checks to run and when — timing is relative to application / lease.',
    },
    qualification: {
        label: 'Qualification',
        summary: 'Thresholds and which conditional approval packages to offer.',
    },
    lease: {
        label: 'Lease',
        summary: 'Lease package and signing.',
    },
    future_resident: {
        label: 'Future Resident',
        summary: 'Terminal — leased.',
        alwaysOn: true,
    },
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        paddingBottom: theme.spacing(1),
    },
    timeline: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
    },
    phaseRow: {
        display: 'flex',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.25, 1.5),
    },
    rail: {
        width: 16,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    dot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        marginTop: 4,
        flexShrink: 0,
    },
    railLine: {
        flex: 1,
        width: 2,
        backgroundColor: theme.palette.divider,
        marginTop: 4,
        minHeight: 12,
    },
    phaseBody: {
        flex: 1,
        minWidth: 0,
    },
    phaseHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(0.75),
    },
    phaseLabel: {
        fontWeight: 700,
        fontSize: '0.9375rem',
    },
    phaseSummary: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        marginTop: 2,
    },
    phaseControls: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    controlRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    select: {
        minWidth: 220,
        maxWidth: '100%',
        fontSize: '0.8125rem',
        backgroundColor: theme.palette.common.white,
    },
    checkChip: {
        height: 24,
        fontWeight: 600,
    },
    checkBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.75),
        borderRadius: 6,
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.divider}`,
    },
    checkBlockHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    checkTimingRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.35),
        paddingLeft: 2,
    },
    checkTimingLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    timingSelect: {
        minWidth: 220,
        maxWidth: '100%',
        fontSize: '0.8125rem',
        backgroundColor: theme.palette.common.white,
    },
    timingNote: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(0.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
        '& svg': {
            fontSize: 16,
            marginTop: 1,
            flexShrink: 0,
            color: '#1565C0',
        },
    },
    thresholdRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    thresholdField: {
        width: 160,
        backgroundColor: theme.palette.common.white,
        '& .MuiInputBase-input': {
            fontSize: '0.8125rem',
        },
    },
    policyBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.35),
        padding: theme.spacing(0.75, 1),
        borderRadius: 6,
        backgroundColor: theme.palette.common.white,
        border: `1px dashed ${theme.palette.divider}`,
    },
    policyLabelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.25),
    },
    policyLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    policyText: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
    offerBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    offerChipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
    },
    offerChip: {
        height: 28,
        fontWeight: 600,
    },
    docSummaryList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        marginTop: theme.spacing(0.25),
    },
    docSummaryRow: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    infoButton: {
        padding: 2,
        color: theme.palette.text.secondary,
        '& svg': {
            fontSize: 16,
        },
    },
    modeRadioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
    },
    modeRadio: {
        marginLeft: 0,
        marginRight: 0,
        alignItems: 'center',
        '& .MuiFormControlLabel-label': {
            fontSize: '0.8125rem',
            lineHeight: 1.35,
        },
        '& .MuiRadio-root': {
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 4,
            paddingRight: 4,
        },
    },
    gateRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1.5),
        backgroundColor: theme.palette.grey[50],
        borderTop: `1px dashed ${theme.palette.divider}`,
        borderBottom: `1px dashed ${theme.palette.divider}`,
    },
    gateLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
    },
    footnote: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
}))

function defaultGates(): Record<string, GateMode> {
    return {
        'leads→application': 'auto',
        'application→screening': 'auto',
        'application→lease': 'auto',
        'screening→qualification': 'auto',
        'qualification→lease': 'pm_review',
        'qualification→future_resident': 'auto',
        'lease→screening': 'auto',
        'lease→future_resident': 'auto',
    }
}

type ModeOption<T extends string> = { value: T; label: string }

function ModeChoiceGroup<T extends string>({
    className,
    radioClassName,
    value,
    options,
    readOnly,
    onChange,
}: {
    className?: string
    radioClassName?: string
    value: T
    options: ModeOption<T>[]
    readOnly?: boolean
    onChange: (next: T) => void
}) {
    return (
        <RadioGroup
            className={className}
            value={value}
            onChange={(e) => {
                if (readOnly) return
                onChange(e.target.value as T)
            }}
        >
            {options.map((option) => (
                <FormControlLabel
                    key={option.value}
                    className={radioClassName}
                    value={option.value}
                    control={<Radio color="primary" size="small" />}
                    label={option.label}
                    disabled={readOnly}
                />
            ))}
        </RadioGroup>
    )
}

const ACCOUNT_INVITE_OPTIONS: ModeOption<AccountInviteMode>[] = [
    { value: 'auto', label: 'Automatically send account invite' },
    { value: 'manager', label: 'Require manager to send account invite' },
]

const GATE_OPTIONS: ModeOption<GateMode>[] = [
    { value: 'auto', label: 'Automatically advance' },
    { value: 'pm_review', label: 'Require PM review' },
]

export type ComposeTimelineState = {
    placement: ScreenPlacement
    feeTiming: FeeTiming
    creditCheck: boolean
    backgroundCheck: boolean
    creditTiming: CheckTiming
    backgroundTiming: CheckTiming
    creditScoreMin: string
    monthlyIncomeMin: string
    offeredMethods: ConditionalMethodId[]
    accountInviteMode: AccountInviteMode
    gates: Record<string, GateMode>
    applicantBindings: PhaseApplicantBindingsMap
}

export type LeadsPipelineTimelineHandle = {
    getState: () => ComposeTimelineState
    isDirty: () => boolean
    markSaved: () => void
}

function buildTimelineDraft(
    initial: Partial<ComposeTimelineState> | null | undefined,
    placement: ScreenPlacement,
): ComposeTimelineState {
    const checkDefault = defaultCheckTiming(placement)
    return {
        placement: initial?.placement ?? placement,
        feeTiming: initial?.feeTiming ?? 'on_submit',
        creditCheck: initial?.creditCheck ?? true,
        backgroundCheck: initial?.backgroundCheck ?? true,
        creditTiming: initial?.creditTiming ?? checkDefault,
        backgroundTiming: initial?.backgroundTiming ?? checkDefault,
        creditScoreMin: initial?.creditScoreMin ?? '620',
        monthlyIncomeMin: initial?.monthlyIncomeMin ?? '3000',
        offeredMethods: initial?.offeredMethods ?? [],
        accountInviteMode: initial?.accountInviteMode ?? 'auto',
        gates: { ...defaultGates(), ...(initial?.gates || {}) },
        applicantBindings: initial?.applicantBindings ?? {},
    }
}

function serializeTimelineDraft(state: ComposeTimelineState): string {
    const { offeredMethods: _offeredMethods, ...rest } = state
    return JSON.stringify(rest)
}

function timelineIsDirty(
    state: ComposeTimelineState,
    baselineJson: string,
    feeIsZero: boolean,
): boolean {
    const baseline = JSON.parse(baselineJson) as ComposeTimelineState
    const left = feeIsZero
        ? { ...state, feeTiming: 'never' as FeeTiming }
        : state
    const right = feeIsZero
        ? { ...baseline, feeTiming: 'never' as FeeTiming }
        : baseline
    return serializeTimelineDraft(left) !== serializeTimelineDraft(right)
}

type LeadsPipelineTimelineProps = {
    placement: ScreenPlacement
    readOnly?: boolean
    workbookSections?: WorkbookSectionsState
    applicantDataSelected?: boolean
    conditionalApprovalsSelected?: boolean
    conditionalApprovalsPolicy?: ConditionalApprovalsPolicy
    signableDocsSelected?: boolean
    leasePacket?: LeasePacketMember[]
    guarantorPacket?: LeasePacketMember[]
    /** Hydrate from PropertyPipelineConfig.definition.compose_timeline */
    initialTimeline?: Partial<ComposeTimelineState> | null
    /** Application fee amount from this LSC; $0 locks timing to Never. */
    feeAmount?: string
    onDirtyChange?: (dirty: boolean) => void
}

export const LeadsPipelineTimeline = React.forwardRef<
    LeadsPipelineTimelineHandle,
    LeadsPipelineTimelineProps
>(function LeadsPipelineTimeline(
    {
        placement,
        readOnly,
        workbookSections,
        applicantDataSelected = false,
        conditionalApprovalsSelected = false,
        conditionalApprovalsPolicy,
        signableDocsSelected = false,
        leasePacket = [],
        guarantorPacket = [],
        initialTimeline,
        feeAmount,
        onDirtyChange,
    },
    ref,
) {
    const classes = useStyles()
    const [feeTiming, setFeeTiming] = useState<FeeTiming>(
        () => buildTimelineDraft(initialTimeline, placement).feeTiming,
    )
    const [creditCheck, setCreditCheck] = useState(
        () => buildTimelineDraft(initialTimeline, placement).creditCheck,
    )
    const [backgroundCheck, setBackgroundCheck] = useState(
        () => buildTimelineDraft(initialTimeline, placement).backgroundCheck,
    )
    const [creditTiming, setCreditTiming] = useState<CheckTiming>(
        () => buildTimelineDraft(initialTimeline, placement).creditTiming,
    )
    const [backgroundTiming, setBackgroundTiming] = useState<CheckTiming>(
        () => buildTimelineDraft(initialTimeline, placement).backgroundTiming,
    )
    const [creditScoreMin, setCreditScoreMin] = useState(
        () => buildTimelineDraft(initialTimeline, placement).creditScoreMin,
    )
    const [monthlyIncomeMin, setMonthlyIncomeMin] = useState(
        () => buildTimelineDraft(initialTimeline, placement).monthlyIncomeMin,
    )
    const [
        accountInviteMode,
        setAccountInviteMode,
    ] = useState<AccountInviteMode>(
        () => buildTimelineDraft(initialTimeline, placement).accountInviteMode,
    )
    const [gates, setGates] = useState(
        () => buildTimelineDraft(initialTimeline, placement).gates,
    )
    const [
        applicantBindings,
        setApplicantBindings,
    ] = useState<PhaseApplicantBindingsMap>(
        () => buildTimelineDraft(initialTimeline, placement).applicantBindings,
    )
    const hydratedKey = useRef<string>('')
    const baselineRef = useRef(
        serializeTimelineDraft(buildTimelineDraft(initialTimeline, placement)),
    )
    const onDirtyChangeRef = useRef(onDirtyChange)
    onDirtyChangeRef.current = onDirtyChange

    const upfrontMethods = useMemo(
        () => methodsForStage(conditionalApprovalsPolicy, 'upfront'),
        [conditionalApprovalsPolicy],
    )
    const afterFailMethods = useMemo(
        () =>
            methodsForStage(conditionalApprovalsPolicy, 'after_failed_screen'),
        [conditionalApprovalsPolicy],
    )
    const fallbackChoiceMode =
        conditionalApprovalsPolicy?.fallbackChoiceMode ?? 'applicant'

    const feeIsZero = isZeroFeeAmount(feeAmount)
    const effectiveFeeTiming: FeeTiming = feeIsZero ? 'never' : feeTiming

    const getState = useCallback((): ComposeTimelineState => {
        return {
            placement,
            feeTiming: effectiveFeeTiming,
            creditCheck,
            backgroundCheck,
            creditTiming,
            backgroundTiming,
            creditScoreMin,
            monthlyIncomeMin,
            offeredMethods: conditionalApprovalsSelected
                ? [...afterFailMethods]
                : [],
            accountInviteMode,
            gates,
            applicantBindings,
        }
    }, [
        accountInviteMode,
        afterFailMethods,
        applicantBindings,
        backgroundCheck,
        backgroundTiming,
        conditionalApprovalsSelected,
        creditCheck,
        creditScoreMin,
        creditTiming,
        effectiveFeeTiming,
        placement,
        gates,
        monthlyIncomeMin,
    ])

    const applyDraft = (draft: ComposeTimelineState) => {
        setFeeTiming(draft.feeTiming)
        setCreditCheck(draft.creditCheck)
        setBackgroundCheck(draft.backgroundCheck)
        setCreditTiming(draft.creditTiming)
        setBackgroundTiming(draft.backgroundTiming)
        setCreditScoreMin(draft.creditScoreMin)
        setMonthlyIncomeMin(draft.monthlyIncomeMin)
        setAccountInviteMode(draft.accountInviteMode)
        setGates(draft.gates)
        setApplicantBindings(draft.applicantBindings)
        baselineRef.current = serializeTimelineDraft(draft)
        onDirtyChangeRef.current?.(false)
    }

    useEffect(() => {
        const key = `${JSON.stringify(initialTimeline ?? null)}:${placement}`
        if (key === hydratedKey.current) return
        hydratedKey.current = key
        applyDraft(buildTimelineDraft(initialTimeline, placement))
    }, [initialTimeline, placement])

    useEffect(() => {
        if (readOnly) {
            onDirtyChangeRef.current?.(false)
            return
        }
        const dirty = timelineIsDirty(
            getState(),
            baselineRef.current,
            feeIsZero,
        )
        onDirtyChangeRef.current?.(dirty)
    }, [feeIsZero, getState, readOnly])

    useImperativeHandle(
        ref,
        () => ({
            getState,
            isDirty: () =>
                timelineIsDirty(getState(), baselineRef.current, feeIsZero),
            markSaved: () => {
                baselineRef.current = serializeTimelineDraft(getState())
                onDirtyChangeRef.current?.(false)
            },
        }),
        [feeIsZero, getState],
    )

    const phases = useMemo((): PhaseId[] => {
        if (placement === 'before_signing') {
            return [
                'leads',
                'application',
                'screening',
                'qualification',
                'lease',
                'future_resident',
            ]
        }
        return [
            'leads',
            'application',
            'lease',
            'screening',
            'qualification',
            'future_resident',
        ]
    }, [placement])

    const checkTimingNote = useMemo(() => {
        if (!creditCheck || !backgroundCheck) return null
        if (creditTiming === backgroundTiming) return null
        return `Background runs ${CHECK_TIMING_LABELS[
            backgroundTiming
        ].toLowerCase()}; credit / income runs ${CHECK_TIMING_LABELS[
            creditTiming
        ].toLowerCase()}.`
    }, [backgroundCheck, backgroundTiming, creditCheck, creditTiming])

    const gateKey = (from: PhaseId, to: PhaseId) => `${from}→${to}`

    const setGate = (key: string, mode: GateMode) => {
        if (readOnly) return
        setGates((prev) => ({ ...prev, [key]: mode }))
    }

    const setPhaseApplicantBinding = (
        phaseId: string,
        next: PhaseApplicantBinding,
    ) => {
        if (readOnly) return
        setApplicantBindings((prev) => {
            const updated: PhaseApplicantBindingsMap = {
                ...prev,
                [phaseId]: next,
            }
            // Keep Required ⊆ Available-by-this-phase across the timeline when
            // upstream availability is removed.
            const availableThrough = new Set<string>()
            for (const id of phases) {
                const binding = updated[id]
                if (!binding) continue
                const availableHere = new Set(binding.available.map(String))
                const nextRequired = binding.required.filter((sectionId) => {
                    const key = String(sectionId)
                    return availableThrough.has(key) || availableHere.has(key)
                })
                updated[id] = {
                    available: binding.available,
                    required: nextRequired,
                }
                for (const sectionId of binding.available) {
                    availableThrough.add(String(sectionId))
                }
                for (const sectionId of nextRequired) {
                    availableThrough.add(String(sectionId))
                }
            }
            return updated
        })
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.timeline}>
                {phases.map((phaseId, index) => {
                    const phase = PHASE_COPY[phaseId]
                    const next = phases[index + 1]
                    const gKey = next ? gateKey(phaseId, next) : null
                    const gateMode = gKey ? gates[gKey] ?? 'auto' : 'auto'

                    return (
                        <React.Fragment key={phaseId}>
                            <Box className={classes.phaseRow}>
                                <Box className={classes.rail}>
                                    <Box className={classes.dot} />
                                    {next ? (
                                        <Box className={classes.railLine} />
                                    ) : null}
                                </Box>
                                <Box className={classes.phaseBody}>
                                    <Box className={classes.phaseHeader}>
                                        <Typography
                                            className={classes.phaseLabel}
                                        >
                                            {phase.label}
                                        </Typography>
                                        {phase.alwaysOn ? (
                                            <Chip
                                                size="small"
                                                label="Always on"
                                                variant="outlined"
                                            />
                                        ) : null}
                                    </Box>
                                    <Typography
                                        className={classes.phaseSummary}
                                    >
                                        {phase.summary}
                                    </Typography>

                                    {phaseId !== 'future_resident' ? (
                                        <Box className={classes.phaseControls}>
                                            {phaseId === 'leads' ? (
                                                <PhaseControlPanel
                                                    title="Account invite"
                                                    icon={<MailOutlineIcon />}
                                                    tone={
                                                        PHASE_PANEL_TONES.invite
                                                    }
                                                >
                                                    <ModeChoiceGroup
                                                        className={
                                                            classes.modeRadioGroup
                                                        }
                                                        radioClassName={
                                                            classes.modeRadio
                                                        }
                                                        value={
                                                            accountInviteMode
                                                        }
                                                        options={
                                                            ACCOUNT_INVITE_OPTIONS
                                                        }
                                                        readOnly={readOnly}
                                                        onChange={
                                                            setAccountInviteMode
                                                        }
                                                    />
                                                </PhaseControlPanel>
                                            ) : null}

                                            {phaseId === 'application' ? (
                                                <PhaseControlPanel
                                                    title="Fee timing"
                                                    icon={<AttachMoneyIcon />}
                                                    tone={PHASE_PANEL_TONES.fee}
                                                >
                                                    <Select
                                                        className={
                                                            classes.select
                                                        }
                                                        value={
                                                            effectiveFeeTiming
                                                        }
                                                        disabled={
                                                            readOnly ||
                                                            feeIsZero
                                                        }
                                                        onChange={(e) =>
                                                            setFeeTiming(
                                                                e.target
                                                                    .value as FeeTiming,
                                                            )
                                                        }
                                                        variant="outlined"
                                                        margin="dense"
                                                    >
                                                        {(Object.keys(
                                                            FEE_TIMING_LABELS,
                                                        ) as FeeTiming[]).map(
                                                            (key) => (
                                                                <MenuItem
                                                                    key={key}
                                                                    value={key}
                                                                    disabled={
                                                                        feeIsZero &&
                                                                        key !==
                                                                            'never'
                                                                    }
                                                                >
                                                                    {
                                                                        FEE_TIMING_LABELS[
                                                                            key
                                                                        ]
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                    {feeIsZero ? (
                                                        <FormHelperText>
                                                            Fee amount is $0, so
                                                            timing is locked to
                                                            Never. Raise the fee
                                                            to choose when to
                                                            collect it.
                                                        </FormHelperText>
                                                    ) : null}
                                                </PhaseControlPanel>
                                            ) : null}

                                            {phaseId === 'screening' ? (
                                                <PhaseControlPanel
                                                    title="Checks"
                                                    icon={
                                                        <VerifiedUserOutlinedIcon />
                                                    }
                                                    tone={
                                                        PHASE_PANEL_TONES.screening
                                                    }
                                                >
                                                    <Box
                                                        className={
                                                            classes.checkBlock
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                classes.checkBlockHeader
                                                            }
                                                        >
                                                            <Chip
                                                                className={
                                                                    classes.checkChip
                                                                }
                                                                size="small"
                                                                color={
                                                                    creditCheck
                                                                        ? 'primary'
                                                                        : 'default'
                                                                }
                                                                variant={
                                                                    creditCheck
                                                                        ? 'default'
                                                                        : 'outlined'
                                                                }
                                                                label="Credit / income"
                                                                onClick={
                                                                    readOnly
                                                                        ? undefined
                                                                        : () =>
                                                                              setCreditCheck(
                                                                                  (
                                                                                      v,
                                                                                  ) =>
                                                                                      !v,
                                                                              )
                                                                }
                                                            />
                                                        </Box>
                                                        {creditCheck ? (
                                                            <Box
                                                                className={
                                                                    classes.checkTimingRow
                                                                }
                                                            >
                                                                <Typography
                                                                    className={
                                                                        classes.checkTimingLabel
                                                                    }
                                                                >
                                                                    Run when
                                                                </Typography>
                                                                <Select
                                                                    className={
                                                                        classes.timingSelect
                                                                    }
                                                                    value={
                                                                        creditTiming
                                                                    }
                                                                    disabled={
                                                                        readOnly
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setCreditTiming(
                                                                            e
                                                                                .target
                                                                                .value as CheckTiming,
                                                                        )
                                                                    }
                                                                    variant="outlined"
                                                                    margin="dense"
                                                                >
                                                                    {(Object.keys(
                                                                        CHECK_TIMING_LABELS,
                                                                    ) as CheckTiming[]).map(
                                                                        (
                                                                            key,
                                                                        ) => (
                                                                            <MenuItem
                                                                                key={
                                                                                    key
                                                                                }
                                                                                value={
                                                                                    key
                                                                                }
                                                                            >
                                                                                {
                                                                                    CHECK_TIMING_LABELS[
                                                                                        key
                                                                                    ]
                                                                                }
                                                                            </MenuItem>
                                                                        ),
                                                                    )}
                                                                </Select>
                                                            </Box>
                                                        ) : null}
                                                    </Box>

                                                    <Box
                                                        className={
                                                            classes.checkBlock
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                classes.checkBlockHeader
                                                            }
                                                        >
                                                            <Chip
                                                                className={
                                                                    classes.checkChip
                                                                }
                                                                size="small"
                                                                color={
                                                                    backgroundCheck
                                                                        ? 'primary'
                                                                        : 'default'
                                                                }
                                                                variant={
                                                                    backgroundCheck
                                                                        ? 'default'
                                                                        : 'outlined'
                                                                }
                                                                label="Background"
                                                                onClick={
                                                                    readOnly
                                                                        ? undefined
                                                                        : () =>
                                                                              setBackgroundCheck(
                                                                                  (
                                                                                      v,
                                                                                  ) =>
                                                                                      !v,
                                                                              )
                                                                }
                                                            />
                                                            <Tooltip
                                                                title="A background check reviews criminal history and related public records."
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <IconButton
                                                                    className={
                                                                        classes.infoButton
                                                                    }
                                                                    size="small"
                                                                    aria-label="What is a background check?"
                                                                >
                                                                    <InfoOutlinedIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                        {backgroundCheck ? (
                                                            <Box
                                                                className={
                                                                    classes.checkTimingRow
                                                                }
                                                            >
                                                                <Typography
                                                                    className={
                                                                        classes.checkTimingLabel
                                                                    }
                                                                >
                                                                    Run when
                                                                </Typography>
                                                                <Select
                                                                    className={
                                                                        classes.timingSelect
                                                                    }
                                                                    value={
                                                                        backgroundTiming
                                                                    }
                                                                    disabled={
                                                                        readOnly
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setBackgroundTiming(
                                                                            e
                                                                                .target
                                                                                .value as CheckTiming,
                                                                        )
                                                                    }
                                                                    variant="outlined"
                                                                    margin="dense"
                                                                >
                                                                    {(Object.keys(
                                                                        CHECK_TIMING_LABELS,
                                                                    ) as CheckTiming[]).map(
                                                                        (
                                                                            key,
                                                                        ) => (
                                                                            <MenuItem
                                                                                key={
                                                                                    key
                                                                                }
                                                                                value={
                                                                                    key
                                                                                }
                                                                            >
                                                                                {
                                                                                    CHECK_TIMING_LABELS[
                                                                                        key
                                                                                    ]
                                                                                }
                                                                            </MenuItem>
                                                                        ),
                                                                    )}
                                                                </Select>
                                                            </Box>
                                                        ) : null}
                                                    </Box>

                                                    {checkTimingNote ? (
                                                        <Typography
                                                            className={
                                                                classes.timingNote
                                                            }
                                                        >
                                                            <InfoOutlinedIcon />
                                                            {checkTimingNote}
                                                        </Typography>
                                                    ) : null}
                                                </PhaseControlPanel>
                                            ) : null}

                                            {phaseId === 'qualification' ? (
                                                <PhaseControlPanel
                                                    title="Decisioning"
                                                    icon={
                                                        <HowToRegOutlinedIcon />
                                                    }
                                                    tone={
                                                        PHASE_PANEL_TONES.qualification
                                                    }
                                                >
                                                    <Box
                                                        className={
                                                            classes.thresholdRow
                                                        }
                                                    >
                                                        <TextField
                                                            className={
                                                                classes.thresholdField
                                                            }
                                                            label="Credit score min"
                                                            type="number"
                                                            size="small"
                                                            variant="outlined"
                                                            value={
                                                                creditScoreMin
                                                            }
                                                            disabled={readOnly}
                                                            onChange={(e) =>
                                                                setCreditScoreMin(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            inputProps={{
                                                                min: 300,
                                                                max: 850,
                                                            }}
                                                        />
                                                        <TextField
                                                            className={
                                                                classes.thresholdField
                                                            }
                                                            label="Monthly income min"
                                                            type="number"
                                                            size="small"
                                                            variant="outlined"
                                                            value={
                                                                monthlyIncomeMin
                                                            }
                                                            disabled={readOnly}
                                                            onChange={(e) =>
                                                                setMonthlyIncomeMin(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            inputProps={{
                                                                min: 0,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box
                                                        className={
                                                            classes.policyBox
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                classes.policyLabelRow
                                                            }
                                                        >
                                                            <Typography
                                                                className={
                                                                    classes.policyLabel
                                                                }
                                                            >
                                                                Background
                                                                policy
                                                            </Typography>
                                                            <Tooltip
                                                                title="A background check reviews criminal history and related public records."
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <IconButton
                                                                    className={
                                                                        classes.infoButton
                                                                    }
                                                                    size="small"
                                                                    aria-label="What is a background check?"
                                                                >
                                                                    <InfoOutlinedIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                        <Typography
                                                            className={
                                                                classes.policyText
                                                            }
                                                        >
                                                            Prospective tenants
                                                            who show an issue on
                                                            the background check
                                                            require PM review.
                                                        </Typography>
                                                    </Box>

                                                    <Box
                                                        className={
                                                            classes.offerBlock
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                classes.policyLabelRow
                                                            }
                                                        >
                                                            <Typography
                                                                className={
                                                                    classes.policyLabel
                                                                }
                                                            >
                                                                Qualifying
                                                                methods
                                                            </Typography>
                                                            <Tooltip
                                                                title="Configured in Setup → Qualifying Methods. Upfront options feed Applicant Data; after-fail options are used when screening does not pass."
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <IconButton
                                                                    className={
                                                                        classes.infoButton
                                                                    }
                                                                    size="small"
                                                                    aria-label="About qualifying methods"
                                                                >
                                                                    <InfoOutlinedIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                        {!conditionalApprovalsSelected ? (
                                                            <Typography
                                                                className={
                                                                    classes.policyText
                                                                }
                                                            >
                                                                Select
                                                                Qualifying
                                                                Methods in Setup
                                                                to configure how
                                                                applicants
                                                                qualify.
                                                            </Typography>
                                                        ) : (
                                                            <Box
                                                                className={
                                                                    classes.docSummaryList
                                                                }
                                                            >
                                                                <Typography
                                                                    className={
                                                                        classes.docSummaryRow
                                                                    }
                                                                >
                                                                    Upfront:{' '}
                                                                    {upfrontMethods.length ===
                                                                    0
                                                                        ? 'None'
                                                                        : upfrontMethods
                                                                              .map(
                                                                                  (
                                                                                      id,
                                                                                  ) =>
                                                                                      CONDITIONAL_METHOD_META[
                                                                                          id
                                                                                      ]
                                                                                          .label,
                                                                              )
                                                                              .join(
                                                                                  ', ',
                                                                              )}
                                                                </Typography>
                                                                <Typography
                                                                    className={
                                                                        classes.docSummaryRow
                                                                    }
                                                                >
                                                                    After failed
                                                                    screening:{' '}
                                                                    {afterFailMethods.length ===
                                                                    0
                                                                        ? 'None'
                                                                        : afterFailMethods
                                                                              .map(
                                                                                  (
                                                                                      id,
                                                                                  ) =>
                                                                                      CONDITIONAL_METHOD_META[
                                                                                          id
                                                                                      ]
                                                                                          .label,
                                                                              )
                                                                              .join(
                                                                                  ', ',
                                                                              )}
                                                                </Typography>
                                                                <Typography
                                                                    className={
                                                                        classes.docSummaryRow
                                                                    }
                                                                >
                                                                    After-fail
                                                                    choice:{' '}
                                                                    {fallbackChoiceMode ===
                                                                    'property_manager'
                                                                        ? 'Property manager'
                                                                        : 'Applicant'}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </PhaseControlPanel>
                                            ) : null}

                                            {phaseId === 'lease' ? (
                                                <PhaseControlPanel
                                                    title="Lease package"
                                                    icon={
                                                        <DescriptionOutlinedIcon />
                                                    }
                                                    tone={
                                                        PHASE_PANEL_TONES.documents
                                                    }
                                                >
                                                    <Typography
                                                        className={
                                                            classes.policyText
                                                        }
                                                    >
                                                        {!signableDocsSelected
                                                            ? 'Select Signable Documents in Setup to build the lease and guarantor packets.'
                                                            : `${
                                                                  leasePacket.filter(
                                                                      (m) =>
                                                                          !!m.docId,
                                                                  ).length
                                                              } lease · ${
                                                                  guarantorPacket.filter(
                                                                      (m) =>
                                                                          !!m.docId,
                                                                  ).length
                                                              } guarantor (rules resolve at send).`}
                                                    </Typography>
                                                    {signableDocsSelected
                                                        ? [
                                                              ...leasePacket.map(
                                                                  (member) => ({
                                                                      member,
                                                                      packet:
                                                                          'Lease',
                                                                  }),
                                                              ),
                                                              ...guarantorPacket.map(
                                                                  (member) => ({
                                                                      member,
                                                                      packet:
                                                                          'Guarantor',
                                                                  }),
                                                              ),
                                                          ]
                                                              .filter(
                                                                  (row) =>
                                                                      !!row
                                                                          .member
                                                                          .docId,
                                                              )
                                                              .map((row) => (
                                                                  <Typography
                                                                      key={
                                                                          row
                                                                              .member
                                                                              .id
                                                                      }
                                                                      className={
                                                                          classes.docSummaryRow
                                                                      }
                                                                  >
                                                                      {
                                                                          row.packet
                                                                      }{' '}
                                                                      ·{' '}
                                                                      {describeLeasePacketRule(
                                                                          row
                                                                              .member
                                                                              .rule,
                                                                      )}
                                                                  </Typography>
                                                              ))
                                                        : null}
                                                </PhaseControlPanel>
                                            ) : null}

                                            <PhaseApplicantDataBindings
                                                phaseId={phaseId}
                                                phaseIds={phases}
                                                phaseIndex={index}
                                                bindings={applicantBindings}
                                                sections={workbookSections}
                                                applicantDataSelected={
                                                    applicantDataSelected
                                                }
                                                readOnly={readOnly}
                                                onChange={
                                                    setPhaseApplicantBinding
                                                }
                                            />
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>

                            {next && gKey ? (
                                <Box className={classes.gateRow}>
                                    <Typography className={classes.gateLabel}>
                                        After this phase →{' '}
                                        {PHASE_COPY[next].label}
                                    </Typography>
                                    <ModeChoiceGroup
                                        className={classes.modeRadioGroup}
                                        radioClassName={classes.modeRadio}
                                        value={gateMode}
                                        options={GATE_OPTIONS}
                                        readOnly={readOnly}
                                        onChange={(mode) => setGate(gKey, mode)}
                                    />
                                </Box>
                            ) : null}
                        </React.Fragment>
                    )
                })}
            </Box>

            <Typography className={classes.footnote}>
                Screening placement comes from the template (or scratch choice
                at create). Applicant Data, fee policy, guest cards, conditional
                approvals, and signable document roles stay independent Setup
                artifacts — Compose binds and summarizes them.
            </Typography>
        </Box>
    )
})
